import { ApiClientError, UnauthenticatedError } from "@/api/client";

/**
 * Server error → plain Ukrainian sentence telling the user what to do.
 *
 * Shared by the create drawers (complaint / contract / interaction). The raw
 * backend text is NEVER shown: the lookup key is the stable backend contract —
 * the body `code` when the endpoint sends one, otherwise the raw exception
 * message (X-Status-Reason). Unknown → generic sentence + a small technical code.
 */
export interface FriendlyError {
  message: string;
  /** Technical code, shown small — only for unknown/unmapped errors. */
  code: string | null;
}

const CLIENT_OUT_OF_SCOPE =
  "Цей клієнт не закріплений ні за вами, ні за вашою командою. Оберіть іншого клієнта або зверніться до керівника.";
const ACCOUNT_REQUIRED = "Оберіть клієнта зі списку.";
const ACCOUNT_NOT_FOUND =
  "Клієнта не знайдено — можливо, його об'єднали або видалили. Оновіть сторінку й оберіть клієнта знову.";

const BY_KEY: Record<string, string> = {
  // ── client scope / client ──
  client_out_of_scope: CLIENT_OUT_OF_SCOPE,
  "No access to account.": CLIENT_OUT_OF_SCOPE,
  account_required: ACCOUNT_REQUIRED,
  "accountId required.": ACCOUNT_REQUIRED,
  "accountId is required.": ACCOUNT_REQUIRED,
  account_not_found: ACCOUNT_NOT_FOUND,
  "Account not found.": ACCOUNT_NOT_FOUND,
  // ── complaint ──
  summary_required: "Заповніть короткий опис.",
  description_required: "Заповніть детальний опис.",
  type_invalid: "Оберіть тип рекламації.",
  channel_required: "Оберіть канал, яким надійшла рекламація.",
  received_at_required: "Вкажіть дату й час отримання рекламації.",
  brand_invalid: "Обраного бренду немає в каталозі. Оберіть інший бренд або «Інший бренд».",
  // ── contract ──
  "legalEntityId required.": "Оберіть юр. особу Kormax.",
  "KormaxLegalEntity not found.":
    "Обрану юр. особу Kormax не знайдено. Оновіть сторінку й оберіть її знову.",
  // ── interaction ──
  "plannedAt is required.": "Вкажіть дату й час.",
  "topic is required.": "Заповніть тему.",
  "contactId is required for type=call.": "Для дзвінка оберіть контакт.",
  assignment_not_permitted:
    "Ваша роль не дозволяє планувати зустрічі й дзвінки. Зверніться до керівника.",
  assignee_outside_scope:
    "Відповідальний не входить до вашої команди. Оберіть іншого відповідального.",
  responsible_unavailable:
    "Відповідальний більше недоступний. Оновіть сторінку, оберіть іншого й спробуйте ще раз.",
  report_required_for_client:
    "Спершу заповніть звіт за попередню зустріч із цим клієнтом — після цього можна запланувати нову.",
  anchor_not_in_holding: "Юрособа не належить цьому клієнту. Оберіть клієнта заново.",
  contact_not_accessible: "Цей контакт вам недоступний. Оберіть інший контакт або продовжте без нього.",
  contact_client_mismatch: "Контакт не належить цьому клієнту. Оберіть інший контакт.",
};

const BY_PATTERN: Array<[RegExp, string]> = [
  [
    /^Contract number '.*' already exists/,
    "Договір із таким номером у цієї юр. особи вже є. Змініть номер або залиште поле порожнім — номер призначиться автоматично.",
  ],
  [/^contractType must be one of/, "Оберіть тип договору."],
  [/^Invalid type: must be one of/, "Оберіть тип: зустріч, дзвінок або відео."],
];

export function describeApiError(err: unknown, action = "Не вдалося створити"): FriendlyError {
  if (err instanceof UnauthenticatedError) {
    return { message: "Сесія завершилась. Увійдіть знову й повторіть дію.", code: null };
  }
  if (!(err instanceof ApiClientError)) {
    // fetch() itself failed (offline / DNS / aborted) — no HTTP response at all.
    if (err instanceof TypeError) {
      return {
        message: "Немає зв'язку із сервером. Перевірте інтернет і спробуйте ще раз.",
        code: "network",
      };
    }
    return { message: `${action}. Спробуйте ще раз або напишіть адміністратору.`, code: "client_error" };
  }

  const isHttpFallback = err.code.startsWith("http_");
  const known =
    (!isHttpFallback ? BY_KEY[err.code] : undefined) ??
    BY_KEY[err.message.trim()] ??
    BY_PATTERN.find(([re]) => re.test(err.message.trim()))?.[1];
  if (known) return { message: known, code: null };

  return {
    message: `${action}. Спробуйте ще раз або напишіть адміністратору.`,
    code: isHttpFallback ? `HTTP ${err.status}` : `${err.code} · HTTP ${err.status}`,
  };
}
